import { memo, useMemo, type FC } from "react";
import { ArrowRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import { formatNumber } from "src/utils/numberFormat";
import CampaignStepCard from "src/layouts/Engagements/Campaigns/shared/CampaignStepCard";
import {
  LIST_FIELDS,
  type ColumnMapping,
  type ListFieldKey,
} from "../createList.constants";
import { getFirstRowValue, type ListCleanup, type ParsedCsv } from "../createListCsv";
import { useCreateListLabel } from "../useCreateListLabel";

interface MatchColumnsStepProps {
  csv: ParsedCsv;
  mapping: ColumnMapping;
  cleanup: ListCleanup | null;
  peopleCount: number;
  showErrors: boolean;
  onChange: (_field: ListFieldKey, _header: string | null) => void;
}

const MatchColumnsStep: FC<MatchColumnsStepProps> = ({
  csv,
  mapping,
  cleanup,
  peopleCount,
  showErrors,
  onChange,
}) => {
  const { t, i18n } = useTranslation();
  const label = useCreateListLabel();
  const formatValue = (value: number) => formatNumber(value, {}, i18n.language);

  const headerOptions = useMemo(
    () => csv.headers.map(header => ({ value: header, label: header })),
    [csv.headers]
  );
  const missingPhone = mapping.phone ? cleanup?.missingPhoneCount ?? 0 : peopleCount;

  return (
    <CampaignStepCard title={label("match.title")} subtitle={label("match.subtitle")}>
      <div className="create-list__mapping" role="table">
        <div className="create-list__mapping-row create-list__mapping-row--head" role="row">
          <span role="columnheader">{label("match.fileColumns")}</span>
          <span aria-hidden />
          <span role="columnheader">{label("match.datatramFields")}</span>
          <span role="columnheader">{label("match.examples")}</span>
        </div>

        {LIST_FIELDS.map(field => {
          const selected = mapping[field.key];
          const hasError = showErrors && field.required && !selected;
          return (
            <div key={field.key} className="create-list__mapping-row" role="row">
              <span className="create-list__mapping-field" role="cell">
                <span>
                  {t(field.labelKey)}
                  {field.required && <span className="campaign-flow-field__required">*</span>}
                </span>
                {field.hintKey && (
                  <span className="create-list__mapping-hint">{t(field.hintKey)}</span>
                )}
              </span>
              <ArrowRight size={16} className="create-list__mapping-arrow" aria-hidden />
              <span role="cell">
                <StyledDropdown
                  value={selected ?? undefined}
                  placeholder={label("match.selectPlaceholder")}
                  allowClear={!field.required}
                  status={hasError ? "error" : undefined}
                  aria-label={t(field.labelKey)}
                  options={headerOptions}
                  onChange={(value?: string) => onChange(field.key, value ?? null)}
                  className="campaign-flow__select"
                  popupClassName="campaign-flow__popup"
                />
              </span>
              <span className="create-list__mapping-example" role="cell" dir="auto">
                {getFirstRowValue(csv, selected)}
              </span>
            </div>
          );
        })}
      </div>

      {mapping.email && (
        <div className="create-list__notices">
          <p className="create-list__notice create-list__notice--success">
            <span className="create-list__notice-icon" aria-hidden>
              <Check size={12} strokeWidth={3} />
            </span>
            <span>
              <strong>{label("match.readyForEmail")}</strong>{" "}
              {label("match.emailMatched", { count: peopleCount, value: formatValue(peopleCount) })}
            </span>
          </p>
          {missingPhone > 0 && (
            <p className="create-list__notice create-list__notice--warning">
              <span className="create-list__notice-icon" aria-hidden>
                !
              </span>
              <span>
                <strong>
                  {label("match.noPhone", { count: missingPhone, value: formatValue(missingPhone) })}
                </strong>{" "}
                {label("match.noPhoneDetail")}
              </span>
            </p>
          )}
        </div>
      )}
    </CampaignStepCard>
  );
};

export default memo(MatchColumnsStep);
